import * as React from 'react';

import {
  enumLoadStatus,
  enumUnit,
  LoadStatus,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { Col } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { v4 as uuidv4 } from 'uuid';

import { ItemsInterface } from '../../_shared/ItemsInterface';
import { SuggestedItemPrice } from '../../_shared/ItemsInterface/_state';
import {
  enumLineItemKind,
  ItemsListEntry,
  NormalizedLineItem,
} from '../../_shared/ItemsInterface/_types';
import { QuickAddMaterialButtons } from '../shared/QuickAddMaterialButtons';

import { salesOrderLineItemsQueryAtom } from '~/modules/trade/_graphql/querySalesOrderLineItems';
import {
  $materialsLocalEntriesById,
  $setMaterialsDirty,
  $setMaterialsLocalEntries,
} from '~/modules/trade/_state/materialsForm.state';
import { $setAutoFocusRowId } from '~/modules/trade/_state/materialsInterface.state';
import {
  $outboundCustomerFormById,
  DEFAULT_OUTBOUND_CUSTOMER_FORM,
} from '~/modules/trade/_state/scaleTicketOutboundCustomerForm.state';
import {
  $outboundWeightUnitMode,
  $saveOutboundMaterials,
  createOutboundDeductionChangeHandler,
  normalizeOutboundEntries,
} from '~/modules/trade/_state/scaleTicketOutboundMaterialsForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';
import { toMoney } from '~/modules/trade/utils/money';
import { zeroWeight } from '~/modules/trade/utils/weight';

type OutboundMaterialsEditorProps = {
  ticket: LoadRecord;
  disabled?: boolean;
};

const INVENTORY_LOCKED_STATUSES: LoadStatus[] = [
  enumLoadStatus.Shipped,
  enumLoadStatus.Received,
  enumLoadStatus.Voided,
  enumLoadStatus.Reconciled,
];

const EMPTY_SO_LINE_ITEMS: never[] = [];

//
// ⚛️ ⚛️ ⚛️ OUTBOUND MATERIALS EDITOR ⚛️ ⚛️ ⚛️
//
export const OutboundMaterialsEditor = ({
  ticket,
  disabled = false,
}: OutboundMaterialsEditorProps) => {
  // Translations
  const t = useTranslations();

  // Refs
  const hasAutoInsertedRef = React.useRef(false);
  const isAutoMergingRef = React.useRef(false);
  const localEntriesRef = React.useRef<ItemsListEntry[]>([]);

  // Atoms
  const weightUnitMode = useAtomValue($outboundWeightUnitMode);
  const atomEntries = useAtomValue($materialsLocalEntriesById).get(ticket.id);
  const fallbackEntries = React.useMemo(
    () =>
      normalizeOutboundEntries(ticket.lineItems ?? [], ticket.deductions ?? []),
    [ticket.lineItems, ticket.deductions],
  );
  const localEntries = atomEntries ?? fallbackEntries;
  localEntriesRef.current = localEntries;
  const setLocalEntries = useSetAtom($setMaterialsLocalEntries);
  const setDirty = useSetAtom($setMaterialsDirty);
  const setAutoFocusRowId = useSetAtom($setAutoFocusRowId);
  const saveOutboundMaterials = useSetAtom($saveOutboundMaterials);
  const customerFormMap = useAtomValue($outboundCustomerFormById);
  const customerForm =
    (ticket?.id ? customerFormMap.get(ticket.id) : undefined) ??
    DEFAULT_OUTBOUND_CUSTOMER_FORM;
  const activeSalesOrderId =
    customerForm.salesOrderId || ticket.salesOrderId || null;

  const $soLineItems = React.useMemo(
    () =>
      salesOrderLineItemsQueryAtom(() => ({
        variables: { salesOrderId: activeSalesOrderId ?? '' },
        skip: !activeSalesOrderId,
      })),
    [activeSalesOrderId],
  );
  const soData = useAtomValue($soLineItems);
  const soLineItems = soData?.data?.lineItems ?? EMPTY_SO_LINE_ITEMS;

  const highlightedMaterialIds = React.useMemo(
    () => soLineItems.map((li) => li.item.id),
    [soLineItems],
  );

  const soDocumentNumber =
    soData?.data?.documentNumber ?? ticket.salesOrderNumber;

  const highlightedSectionTitle = React.useMemo(() => {
    if (!soDocumentNumber || soLineItems.length === 0) return undefined;
    return `${t.scaleTicketPro.materialsTab.salesOrderMaterials} • #${soDocumentNumber}`;
  }, [soDocumentNumber, soLineItems.length, t]);

  const suggestedPrices = React.useMemo(() => {
    const map = new Map<string, SuggestedItemPrice>();
    for (const li of soLineItems) {
      if (li.item?.id && li.unitPrice) {
        map.set(li.item.id, {
          unitPrice: toMoney(li.unitPrice),
          pricingUnit: li.unit as Unit,
        });
      }
    }
    return map;
  }, [soLineItems]);

  React.useEffect(() => {
    if (suggestedPrices.size === 0) return;

    const snapshotEntries = localEntriesRef.current;
    let hasChanges = false;
    const updated = snapshotEntries.map((entry) => {
      if (entry.kind !== enumLineItemKind.LineItem) return entry;
      const item = entry as NormalizedLineItem;
      if (!item.itemId) return entry;
      const suggested = suggestedPrices.get(item.itemId);
      if (!suggested) return entry;
      if (
        item.unitPrice?.amount === suggested.unitPrice.amount &&
        item.unitPrice?.currencyCode === suggested.unitPrice.currencyCode &&
        item.pricingUnit === suggested.pricingUnit
      ) {
        return entry;
      }
      hasChanges = true;
      return {
        ...item,
        unitPrice: suggested.unitPrice,
        pricingUnit: suggested.pricingUnit,
      };
    });

    if (!hasChanges) return;
    if (localEntriesRef.current !== snapshotEntries) return;

    isAutoMergingRef.current = true;
    setLocalEntries({ ticketId: ticket.id, entries: updated });
    setDirty({ ticketId: ticket.id, dirty: true });
    saveOutboundMaterials({
      ticketId: ticket.id,
      failedToSaveMessage: t.scaleTicketPro.materialsTab.failedToSaveMaterials,
    });
    requestAnimationFrame(() => {
      isAutoMergingRef.current = false;
    });
  }, [
    suggestedPrices,
    ticket.id,
    setLocalEntries,
    setDirty,
    saveOutboundMaterials,
    t,
  ]);

  const inventoryLocked = INVENTORY_LOCKED_STATUSES.includes(
    ticket.status as LoadStatus,
  );
  const serverHasLineItems = ticket.lineItems.length > 0;

  // Callbacks
  const handleUpdateEntries = React.useCallback(
    (updatedEntries: ItemsListEntry[]) => {
      setLocalEntries({ ticketId: ticket.id, entries: updatedEntries });
      setDirty({ ticketId: ticket.id, dirty: true });
      saveOutboundMaterials({
        ticketId: ticket.id,
        failedToSaveMessage:
          t.scaleTicketPro.materialsTab.failedToSaveMaterials,
      });
    },
    [setLocalEntries, setDirty, saveOutboundMaterials, ticket.id, t],
  );

  const handleAddItemFromQuickAdd = React.useCallback(
    (type: 'Raw' | 'Tagged', data?: Partial<NormalizedLineItem>) => {
      const newItem: NormalizedLineItem = {
        kind: enumLineItemKind.LineItem,
        id: data?.id ?? `temp-${uuidv4()}`,
        type,
        itemId: data?.itemId ?? null,
        tagId: data?.tagId ?? null,
        weights: data?.weights ?? {
          gross: zeroWeight(),
          tare: zeroWeight(),
          net: zeroWeight(),
        },
        unitPrice: data?.unitPrice ?? null,
        pricingUnit: data?.pricingUnit ?? enumUnit.Pounds,
        notes: data?.notes,
        sourceLineId: data?.sourceLineId,
      };
      const updated = [...localEntries, newItem];
      setLocalEntries({ ticketId: ticket.id, entries: updated });
      setDirty({ ticketId: ticket.id, dirty: true });
      saveOutboundMaterials({
        ticketId: ticket.id,
        failedToSaveMessage:
          t.scaleTicketPro.materialsTab.failedToSaveMaterials,
      });
    },
    [
      localEntries,
      setLocalEntries,
      setDirty,
      saveOutboundMaterials,
      ticket.id,
      t,
    ],
  );

  React.useEffect(() => {
    hasAutoInsertedRef.current = false;
  }, [ticket.id]);

  React.useEffect(() => {
    if (hasAutoInsertedRef.current) return;
    hasAutoInsertedRef.current = true;
    if (localEntries.length > 0) return;
    if (serverHasLineItems) return;
    if (inventoryLocked) return;
    if (disabled) return;

    const newId = `temp-${uuidv4()}`;
    handleAddItemFromQuickAdd('Raw', { id: newId });
    setAutoFocusRowId(newId);
  }, [
    localEntries.length,
    serverHasLineItems,
    inventoryLocked,
    disabled,
    handleAddItemFromQuickAdd,
    setAutoFocusRowId,
  ]);

  const handleDeductionChange = React.useMemo(
    () =>
      ticket.facilityId
        ? createOutboundDeductionChangeHandler(ticket.id, ticket.facilityId)
        : undefined,
    [ticket.id, ticket.facilityId],
  );

  const sidebarContent = React.useMemo(
    () =>
      !inventoryLocked ? (
        <QuickAddMaterialButtons
          onAddItem={handleAddItemFromQuickAdd}
          onFocusNewItem={setAutoFocusRowId}
          maxItems={5}
        />
      ) : null,
    [inventoryLocked, handleAddItemFromQuickAdd, setAutoFocusRowId],
  );

  return (
    <Col className="@OutboundMaterialsEditor h-full" gap1>
      <div className="min-h-0 flex-1">
        <ItemsInterface
          showPricing
          entries={localEntries}
          onUpdateEntries={handleUpdateEntries}
          disabled={disabled}
          inventoryLocked={inventoryLocked}
          facilityDefaultUnit={enumUnit.Pounds}
          weightUnitMode={weightUnitMode === 'scale' ? 'scale' : 'price'}
          ticketId={ticket.id}
          ticketStatus={ticket.status}
          heavyWeight={ticket.heavyWeight}
          lightWeight={ticket.lightWeight}
          sidebarContent={sidebarContent}
          onDeductionChange={handleDeductionChange}
          highlightedMaterialIds={highlightedMaterialIds}
          highlightedSectionTitle={highlightedSectionTitle}
          suggestedPrices={suggestedPrices}
        />
      </div>
    </Col>
  );
};
